import React, { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '../../stores/editorStore';
import type { EditorModule } from '../../types/editor';
import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import styles from './AppShell.module.css';

const AiArchitectPanel = lazy(() => import('../ai-architect/AiArchitectPanel').then(m => ({ default: m.AiArchitectPanel })));
const WeaponEditor = lazy(() => import('../weapons/WeaponEditor'));
const EnemyEditor = lazy(() => import('../enemies/EnemyEditor'));
const PerkEditor = lazy(() => import('../perks/PerkEditor'));
const PowerupEditor = lazy(() => import('../powerups/PowerupEditor'));
const CombatSystemEditor = lazy(() => import('../combat/CombatSystemEditor'));
const SpawnerEditor = lazy(() => import('../spawner/SpawnerEditor'));
const XpLevelEditor = lazy(() => import('../xp-system/XpLevelEditor'));
const GameModeEditor = lazy(() => import('../game-modes/GameModeEditor'));
const ViewportCanvas = lazy(() => import('../viewport/ViewportCanvas'));
const SpriteSheetManager = lazy(() => import('../sprite/SpriteSheetManager'));
const AnimationTimeline = lazy(() => import('../animation/AnimationTimeline'));
const EntityInspector = lazy(() => import('../entity/EntityInspector'));
const TurretTrapEditor = lazy(() => import('../advanced/TurretTrapEditor'));
const CraftingEditor = lazy(() => import('../advanced/CraftingEditor'));
const CompanionEditor = lazy(() => import('../advanced/CompanionEditor'));
const EnvironmentEditor = lazy(() => import('../advanced/EnvironmentEditor'));
const MetaProgressionEditor = lazy(() => import('../advanced/MetaProgressionEditor'));
const JsonExportPanel = lazy(() => import('../export/JsonExportPanel'));

const MODULE_MAP: Record<EditorModule, React.LazyExoticComponent<React.ComponentType>> = {
  'ai-architect': AiArchitectPanel,
  'weapons': WeaponEditor,
  'enemies': EnemyEditor,
  'perks': PerkEditor,
  'powerups': PowerupEditor,
  'combat': CombatSystemEditor,
  'spawner': SpawnerEditor,
  'xp-system': XpLevelEditor,
  'game-modes': GameModeEditor,
  'viewport': ViewportCanvas,
  'sprite': SpriteSheetManager,
  'animation': AnimationTimeline,
  'entity': EntityInspector,
  'turrets': TurretTrapEditor,
  'crafting': CraftingEditor,
  'companions': CompanionEditor,
  'environment': EnvironmentEditor,
  'meta-progression': MetaProgressionEditor,
  'export': JsonExportPanel,
};

function LoadingFallback() {
  const { t } = useTranslation();
  return (
    <div className={styles.loading}>
      <div className={styles.spinner} />
      {t('common.loading')}
    </div>
  );
}

export function AppShell() {
  const activeModule = useEditorStore((s) => s.activeModule);
  const ActiveComponent = MODULE_MAP[activeModule];

  return (
    <div className={styles.shell}>
      <TopBar />
      <div className={styles.body}>
        <Sidebar />
        <main className={styles.content}>
          <Suspense fallback={<LoadingFallback />}>
            {ActiveComponent && <ActiveComponent />}
          </Suspense>
        </main>
      </div>
    </div>
  );
}
